/**
 * DialogueBox
 *
 * Glass dialogue blade: character nameplate, player "Vous", internal thought, continue chevron.
 */

import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { isTypingInEditableField } from '../systems/keyboard-utils';
import { splitCharacterDisplayName } from '../systems/ally-trust';
import { useTypewriter } from '../systems/use-typewriter';
import { useAccessibility } from '../systems/accessibility-context';
import { CharacterPortrait } from '../types';
import { FormattedDialogueText, getDialogueVisibleText } from './formatted-dialogue-text';
import styles from './dialogue-box.module.css';

interface DialogueBoxProps {
  speaker?: string;
  text: string;
  onClick: () => void;
  showContinue: boolean;
  characterId?: string;
  emotion?: string;
  isInternal?: boolean;
  characterData?: CharacterPortrait[];
  /** When 'autre', apply highlight to the explanation zone (feedback: what went wrong is context/action) */
  highlightZone?: 'email' | 'autre';
  /** When false, Space/Enter do not advance (e.g. menu open on top) */
  keyboardActive?: boolean;
  /** Extra class, e.g. to sit in a parent stack instead of the default overlay. */
  className?: string;
  /** Enter / exit fade driven by QuickFadePresence. */
  uiFade?: 'in' | 'out';
  /** When false, show the full text immediately (e.g. sticky line above a choice). */
  enableTyping?: boolean;
}

function ThoughtIcon() {
  return (
    <svg
      className={styles.thoughtIcon}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M7 17c-2.2 0-4-1.7-4-3.9 0-1.7 1.1-3.2 2.7-3.7C5.9 6.9 8 5 10.6 5c2.4 0 4.4 1.6 4.9 3.8h.3c2.3 0 4.2 1.8 4.2 4.1S18.1 17 15.8 17Z" />
      <circle cx="5.5" cy="19.5" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

function resolveNameplate(
  isInternal: boolean,
  characterId: string | undefined,
  speaker: string | undefined,
  character: CharacterPortrait | null,
): { givenName: string; role: string } | null {
  if (isInternal) return null;

  if (characterId) {
    return splitCharacterDisplayName(
      speaker?.trim() || character?.name || characterId,
    );
  }

  const rawName = speaker?.trim() ?? '';
  if (rawName) return splitCharacterDisplayName(rawName);

  return { givenName: 'Vous', role: '' };
}

export const DialogueBox = memo(({
  speaker,
  text,
  onClick,
  showContinue,
  characterId,
  isInternal = false,
  characterData = [],
  highlightZone,
  keyboardActive = true,
  className,
  uiFade = 'in',
  enableTyping = true,
}: DialogueBoxProps) => {
  const { strictMode: a11yStrict } = useAccessibility();
  const visibleText = useMemo(() => getDialogueVisibleText(text), [text]);
  const { displayedText, isTyping, skipTyping } = useTypewriter(visibleText, {
    speed: 15,
    enabled: enableTyping && !a11yStrict,
  });

  const lastClickTimeRef = useRef<number>(0);

  const handleClick = useCallback(() => {
    const now = Date.now();
    // Prevent clicks faster than 100ms to avoid accidental double-advancement
    if (now - lastClickTimeRef.current < 100) return;
    lastClickTimeRef.current = now;

    if (isTyping) {
      skipTyping();
    } else {
      onClick();
    }
  }, [isTyping, skipTyping, onClick]);

  useEffect(() => {
    if (!keyboardActive) return;

    const handleWindowKeyDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.key !== 'Enter' && e.key !== 'ArrowRight') return;
      if (isTypingInEditableField()) return;
      e.preventDefault();
      handleClick();
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [keyboardActive, handleClick]);

  const character = characterId
    ? characterData.find((c) => c.id === characterId) ?? null
    : null;
  const nameplate = resolveNameplate(isInternal, characterId, speaker, character);
  const showChevron = showContinue && !isTyping;

  const dialogueBoxClass = [
    styles.dialogueBox,
    uiFade === 'out' ? styles.dialogueFadeOut : styles.dialogueFadeIn,
    isInternal && styles.internalDialogue,
    highlightZone === 'autre' && styles.dialogueBoxHighlight,
    showChevron && styles.hasContinue,
    className,
  ].filter(Boolean).join(' ');

  const ariaSpeaker = isInternal
    ? 'pensée'
    : (nameplate?.givenName ?? speaker ?? 'dialogue');

  return (
    <div
      className={dialogueBoxClass}
      onClick={handleClick}
      tabIndex={-1}
      role="button"
      aria-label={`Dialogue from ${ariaSpeaker}: ${visibleText}`}
    >
      {nameplate && (
        <div className={styles.speakerChip} aria-hidden="true">
          <span className={styles.speakerGiven}>{nameplate.givenName}</span>
          {nameplate.role && (
            <span className={styles.speakerRole}>{nameplate.role}</span>
          )}
        </div>
      )}

      <div className={`${styles.dialogueText} ${isInternal ? styles.internalText : ''}`}>
        {isInternal && <ThoughtIcon />}
        <FormattedDialogueText text={text} visibleLength={displayedText.length} />
        {isTyping && <span className={styles.typingCursor}>|</span>}
      </div>

      {showChevron && (
        <span className={styles.continueChevron} aria-hidden="true" />
      )}
    </div>
  );
});

DialogueBox.displayName = 'DialogueBox';
